import React, { useCallback, useEffect } from "react";

import { mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select from "react-select";

import { DEFAULT_MOST_VIEWED_ITEM_TYPE } from "../../../constants/mostViewed";
import Slider from "../../../pages/platform/components/Slider";
import { getCountryOptions } from "../../../utils/data/Countries";
import {
  isCarouselComponent,
  isMosaicComponent,
} from "../../../utils/typePredicate";
import SwitchOnOff from "../../common/tools/SwitchOnOff";
import Tooltip from "../../tooltip/Tooltip";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type { TypeOption } from "./mostViewedSelect.types";
import type {
  ComponentWithDataSourceParams,
  MostViewedParams,
} from "../../../api/ProjectAPI.types";
import type { AppState } from "../../../reducers";
import type {
  AllCountries,
  CountryOption,
} from "../../../utils/data/Countries";

interface MostViewedSelectProps {
  item: ComponentWithDataSourceParams<MostViewedParams>;
  onChange: (value: ComponentWithDataSourceParams<MostViewedParams>) => void;
}

interface PeriodOption {
  value: MostViewedParams["period"];
  label: string;
}

export default function MostViewedSelect({
  onChange,
  item,
}: MostViewedSelectProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const countries = useSelector(
    (state: AppState) => state.fetchReducer.countries,
  );

  const typeOptionList: TypeOption[] = [
    { value: "media", label: t("mostViewedType.media") },
    { value: "series", label: t("mostViewedType.series") },
  ];

  const periodOptionList: PeriodOption[] = [
    { value: "week", label: t("mostViewedPeriod.week") },
    { value: "month", label: t("mostViewedPeriod.month") },
    { value: "threeMonths", label: t("mostViewedPeriod.threeMonths") },
    { value: "year", label: t("mostViewedPeriod.year") },
    { value: "all", label: t("mostViewedPeriod.all") },
  ];

  const countryOptions = getCountryOptions(countries, true);

  const isDisabledNumberOnItems =
    item.type !== "carousel" || item.params.ratio !== "34";

  const handleChangeNumberOnItems = useCallback(
    (value: MostViewedParams["hasNumberOnItems"]): void => {
      if (isCarouselComponent(item)) {
        onChange({
          ...item,
          params: {
            ...item.params,
            hasNumberOnItems: value,
          },
        });
      }
    },
    [item, onChange],
  );

  useEffect(() => {
    if (isDisabledNumberOnItems && item.params.hasNumberOnItems) {
      handleChangeNumberOnItems(false);
    }
  }, [isDisabledNumberOnItems, item, handleChangeNumberOnItems]);

  const handleChangeType = (selectedOption: SingleValue<TypeOption>): void => {
    if (isCarouselComponent(item)) {
      onChange({
        ...item,
        params: {
          ...item.params,
          itemType: selectedOption
            ? selectedOption.value
            : DEFAULT_MOST_VIEWED_ITEM_TYPE,
        },
      });
    }
    if (isMosaicComponent(item)) {
      onChange({
        ...item,
        params: {
          ...item.params,
          itemType: selectedOption
            ? selectedOption.value
            : DEFAULT_MOST_VIEWED_ITEM_TYPE,
        },
      });
    }
  };

  const handleChangePeriod = (
    selectedOption: SingleValue<PeriodOption>,
  ): void => {
    if (isCarouselComponent(item)) {
      onChange({
        ...item,
        params: {
          ...item.params,
          period: selectedOption ? selectedOption.value : "week",
        },
      });
    }
    if (isMosaicComponent(item)) {
      onChange({
        ...item,
        params: {
          ...item.params,
          period: selectedOption ? selectedOption.value : "week",
        },
      });
    }
  };

  const handleChangeMaxItems = (value: MostViewedParams["maxItems"]): void => {
    if (isCarouselComponent(item)) {
      onChange({
        ...item,
        params: {
          ...item.params,
          maxItems: value,
        },
      });
    }
    if (isMosaicComponent(item)) {
      onChange({
        ...item,
        params: {
          ...item.params,
          maxItems: value,
        },
      });
    }
  };

  const handleChangeCountry = (
    selectedOption: SingleValue<AllCountries | CountryOption>,
  ): void => {
    if (isCarouselComponent(item)) {
      onChange({
        ...item,
        params: {
          ...item.params,
          countryId: selectedOption ? selectedOption.value : null,
        },
      });
    }
    if (isMosaicComponent(item)) {
      onChange({
        ...item,
        params: {
          ...item.params,
          countryId: selectedOption ? selectedOption.value : null,
        },
      });
    }
  };

  return (
    <div className="flex max-w-screen-sm flex-col gap-[0.78125rem]">
      <div>
        <label className="font-normal" htmlFor="mostViewedType">
          {t("mostViewedType.label")}
        </label>
        <Select
          className="editor-select"
          classNamePrefix="slct"
          name="mostViewedType"
          onChange={handleChangeType}
          options={typeOptionList}
          value={typeOptionList.find(
            (option) => option.value === item.params.itemType,
          )}
        />
      </div>
      <div className="my-[0.3125rem] flex items-center gap-[0.625rem]">
        <SwitchOnOff
          checked={item.params.hasNumberOnItems || false}
          disabled={isDisabledNumberOnItems}
          name="hasNumberOnItems"
          onChange={(value: boolean): void => handleChangeNumberOnItems(value)}
          size="sm"
        />
        <label className="m-0 font-normal" htmlFor="hasNumberOnItems">
          {t("displayNumberOnItems")}
        </label>
        <span
          className="cursor-pointer"
          data-tooltip-id="id-most-viewed-number-on-items"
        >
          <Icon path={mdiInformationOutline} size={0.9375} />
        </span>
        <Tooltip
          content={t("tooltipInfoNumberOnItems")}
          id="id-most-viewed-number-on-items"
        />
      </div>
      <div>
        <label className="font-normal" htmlFor="mostViewedPeriod">
          {t("mostViewedPeriod.label")}
        </label>
        <Select
          className="editor-select"
          classNamePrefix="slct"
          name="mostViewedPeriod"
          onChange={handleChangePeriod}
          options={periodOptionList}
          placeholder={t("mostViewedPeriod.placeholder")}
          value={
            item.params.period
              ? periodOptionList.find(
                  (option) => option.value === item.params.period,
                )
              : periodOptionList[0]
          }
        />
      </div>
      <div>
        <label className="font-normal" htmlFor="mostViewedCountry">
          {t("country")}
        </label>
        <Select
          isClearable
          className="editor-select"
          classNamePrefix="slct"
          name="mostViewedCountry"
          onChange={handleChangeCountry}
          options={countryOptions}
          placeholder={t("placeholderCountry")}
          value={countryOptions.find(
            (option) => option.value === item.params?.countryId,
          )}
        />
      </div>
      <div>
        <label className="font-normal" htmlFor="mostViewedMaxItems">
          {t("mostViewedMaxItems")}
        </label>
        <Slider
          max={20}
          min={5}
          step={5}
          value={item.params.maxItems || 10}
          onChange={(value): void =>
            handleChangeMaxItems(value as MostViewedParams["maxItems"])
          }
        />
      </div>
    </div>
  );
}
